import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import styles from "./barChart.module.scss";

interface BarConfig {
  dataKey: string;
  color: string;
  label: string;
}

interface BarChartProps {
  title?: string;
  data?: any[];
  xAxisKey?: string;
  bars?: BarConfig[];
  height?: number;
}

const CustomBarChart: React.FC<BarChartProps> = ({
  title = "Bar Chart",
  data = [],
  xAxisKey = "year",
  bars = [
    { dataKey: "closed", color: "#00d2aa", label: "Closed" },
    { dataKey: "inProcess", color: "#5b6b9e", label: "In-Process" },
    { dataKey: "expired", color: "#ff6b6b", label: "Expired" },
  ],
  height = 300,
}) => {
  // Calculate dynamic domain and ticks based on data
  const { maxValue, ticks } = useMemo(() => {
    if (!data || data.length === 0) {
      return { maxValue: 100, ticks: [0, 25, 50, 75, 100] };
    }

    let max = 0;
    data.forEach((item) => {
      bars.forEach((bar) => {
        const value = item[bar.dataKey];
        if (typeof value === "number" && value > max) {
          max = value;
        }
      });
    });

    // Round up to nearest multiple of 10
    const roundedMax = Math.ceil(max / 10) * 10 + 20;
    const tickInterval = Math.ceil(roundedMax / 4 / 10) * 10;
    const calculatedTicks = Array.from(
      { length: 5 },
      (_, i) => i * tickInterval
    );

    return { maxValue: roundedMax, ticks: calculatedTicks };
  }, [data, bars]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box className={styles.customTooltip}>
          <Typography className={styles.tooltipLabel}>{label}</Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={`item-${index}`}
              className={styles.tooltipItem}
              style={{ color: entry.color }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const CustomLegend = () => {
    return (
      <Box className={styles.customLegend}>
        {bars.map((bar, index) => (
          <Box key={index} className={styles.legendItem}>
            <Box
              className={styles.legendDot}
              style={{ backgroundColor: bar.color }}
            />
            <Typography className={styles.legendText}>{bar.label}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
        </Box>
        <Box
          className={styles.chartContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={height}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {title}
        </Typography>
      </Box>

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey={xAxisKey}
              tick={{ fill: "#666666", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
            />
            <YAxis
              tick={{ fill: "#666666", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              domain={[0, maxValue]}
              ticks={ticks}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
            />
            {bars.map((bar, index) => (
              <Bar
                key={index}
                dataKey={bar.dataKey}
                fill={bar.color}
                radius={[24, 24, 0, 0]}
                barSize={20}
                name={bar.label}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <CustomLegend />
    </Box>
  );
};

export default CustomBarChart;
